import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Brain,
  Image,
  Search,
  MessageSquare,
  CreditCard,
  ExternalLink,
  CheckCircle,
  AlertTriangle,
  Zap,
  DollarSign,
  Info,
  Settings,
  Key,
  Globe,
  Sparkles,
} from 'lucide-react';

export default function AIServicesPage() {
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsageData();
  }, []);

  const loadUsageData = async () => {
    try {
      // Try to get usage data from API
      const response = await api.get('/ai/usage');
      setUsageData(response.data);
    } catch (e) {
      // Use default data if API not available
      setUsageData({
        total_requests: 150,
        image_generations: 12,
        chat_messages: 138,
        balance_remaining: 45.50
      });
    } finally {
      setLoading(false);
    }
  };

  const aiServices = [
    {
      id: 'claude-sonnet',
      name: 'Claude Sonnet 4.5',
      nameAr: 'كلود سونيت',
      provider: 'Anthropic',
      description: 'محرك الذكاء الاصطناعي الرئيسي للمحادثات وتحليل الملفات',
      descriptionEn: 'Main AI engine for conversations and file analysis',
      icon: Brain,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
      pricing: 'مدفوع',
      pricingType: 'paid',
      priceDetails: '$3 / مليون Token (Input) - $15 / مليون Token (Output)',
      apiKeyType: 'Emergent LLM Key',
      searchId: 'claude-3-5-sonnet-20241022',
      features: [
        'تحليل الملفات (PDF, Word, Excel)',
        'فهم السياق والمحادثات الطويلة',
        'استخراج البيانات من المستندات',
        'صياغة الردود الاحترافية',
        'دعم اللغة العربية والإنجليزية',
      ],
      docsUrl: 'https://docs.anthropic.com/claude/docs',
      status: 'active',
    },
    {
      id: 'ideogram-ai',
      name: 'Ideogram AI',
      nameAr: 'أيديوغرام',
      provider: 'Ideogram',
      description: 'توليد صور احترافية مع نصوص واضحة 100% - الأفضل للتصميمات التجارية',
      descriptionEn: 'Professional image generation with 100% clear text - Best for commercial designs',
      icon: Image,
      color: 'text-pink-500',
      bgColor: 'bg-pink-500/10',
      borderColor: 'border-pink-500/20',
      pricing: 'مدفوع',
      pricingType: 'paid',
      priceDetails: '$0.08 / صورة (Standard) - $0.10 / صورة (HD)',
      apiKeyType: 'Ideogram API Key',
      searchId: 'ideogram-v2',
      features: [
        '✨ نصوص واضحة 100% في الصور',
        'تصميمات 3D واقعية للمنتجات',
        'بوسترات وفلايرات احترافية',
        'شعارات وهويات بصرية',
        'صفحات كتالوج المنتجات',
        'منشورات السوشيال ميديا',
        'أغلفة Company Profile',
      ],
      docsUrl: 'https://ideogram.ai/api',
      status: 'active',
      isConfigured: true,
      configNote: 'مُفعّل - API Key محفوظ في النظام',
      studioUrl: '/design-studio',
    },
    {
      id: 'openai-image',
      name: 'OpenAI GPT Image 1',
      nameAr: 'مولد الصور',
      provider: 'OpenAI',
      description: 'توليد صور واقعية وثلاثية الأبعاد بالذكاء الاصطناعي',
      descriptionEn: 'AI-powered realistic and 3D image generation',
      icon: Image,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
      pricing: 'مدفوع',
      pricingType: 'paid',
      priceDetails: '$0.04 / صورة (1024x1024)',
      apiKeyType: 'Emergent LLM Key',
      searchId: 'gpt-image-1',
      features: [
        'صور واقعية عالية الجودة',
        'تصميمات 3D احترافية',
        'شعارات وهويات بصرية',
        'صور منتجات للكتالوجات',
        'رسومات معمارية',
      ],
      docsUrl: 'https://platform.openai.com/docs/guides/images',
      status: 'active',
    },
    {
      id: 'duckduckgo-search',
      name: 'DuckDuckGo Search',
      nameAr: 'محرك البحث',
      provider: 'DuckDuckGo',
      description: 'البحث على الإنترنت عن الموردين والمنتجات',
      descriptionEn: 'Web search for suppliers and products',
      icon: Search,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/20',
      pricing: 'مجاني',
      pricingType: 'free',
      priceDetails: 'بدون حدود - مجاني تماماً',
      apiKeyType: 'لا يحتاج مفتاح',
      searchId: 'ddgs',
      features: [
        'بحث غير محدود',
        'لا يحتاج API Key',
        'نتائج من جميع أنحاء العالم',
        'دعم البحث بالعربية',
        'خصوصية عالية',
      ],
      docsUrl: 'https://pypi.org/project/ddgs/',
      status: 'active',
    },
  ];

  const paymentOptions = [
    {
      name: 'Emergent Universal Key',
      description: 'مفتاح موحد لجميع خدمات AI (Claude, OpenAI, Gemini)',
      url: 'https://app.emergent.sh',
      icon: Key,
      recommended: true,
      credentials: 'استخدم حساب Emergent الخاص بك',
      credentialsEn: 'Use your Emergent account',
      note: 'بعد الدخول: Profile → Universal Key → Add Balance',
    },
    {
      name: 'Ideogram AI',
      description: 'مفتاح API لخدمة Ideogram - توليد صور بنصوص واضحة',
      url: 'https://ideogram.ai/manage-api',
      icon: Image,
      recommended: true,
      credentials: 'سجّل في Ideogram واحصل على API Key مجاناً',
      credentialsEn: 'Sign up at Ideogram and get free API Key',
      signupUrl: 'https://ideogram.ai',
      note: '✅ مُفعّل حالياً في النظام',
    },
    {
      name: 'OpenAI Direct',
      description: 'الدفع مباشرة لـ OpenAI',
      url: 'https://platform.openai.com/settings/organization/billing/overview',
      icon: CreditCard,
      recommended: false,
      credentials: 'يتطلب حساب OpenAI منفصل - إنشاء حساب جديد مجاني',
      credentialsEn: 'Requires separate OpenAI account - Free to create',
      signupUrl: 'https://platform.openai.com/signup',
    },
    {
      name: 'Anthropic Direct',
      description: 'الدفع مباشرة لـ Anthropic (Claude)',
      url: 'https://console.anthropic.com/settings/plans',
      icon: CreditCard,
      recommended: false,
      credentials: 'يتطلب حساب Anthropic منفصل - إنشاء حساب جديد مجاني',
      credentialsEn: 'Requires separate Anthropic account - Free to create',
      signupUrl: 'https://console.anthropic.com/',
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
              خدمات الذكاء الاصطناعي
            </h1>
            <p className="text-muted-foreground mt-1">AI Services & Billing</p>
          </div>
        </div>
      </div>

      {/* Usage Overview */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            نظرة عامة على الاستخدام
          </CardTitle>
          <CardDescription>Usage Overview</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-background/50 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="h-4 w-4 text-purple-500" />
                <span className="text-sm text-muted-foreground">محادثات</span>
              </div>
              <p className="text-2xl font-bold font-mono">{usageData?.chat_messages || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-background/50 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Image className="h-4 w-4 text-green-500" />
                <span className="text-sm text-muted-foreground">صور مولدة</span>
              </div>
              <p className="text-2xl font-bold font-mono">{usageData?.image_generations || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-background/50 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Search className="h-4 w-4 text-orange-500" />
                <span className="text-sm text-muted-foreground">عمليات بحث</span>
              </div>
              <p className="text-2xl font-bold font-mono">∞</p>
              <span className="text-xs text-green-500">مجاني</span>
            </div>
            <div className="p-4 rounded-lg bg-background/50 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-yellow-500" />
                <span className="text-sm text-muted-foreground">الرصيد المتبقي</span>
              </div>
              <p className="text-2xl font-bold font-mono text-green-500">
                ${usageData?.balance_remaining?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Services List */}
      <div className="space-y-4">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2">
          <Brain className="h-5 w-5" />
          الخدمات المستخدمة
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {aiServices.map((service) => {
            const Icon = service.icon;
            return (
              <Card 
                key={service.id} 
                className={`bg-card border-border hover:${service.borderColor} transition-all`}
                data-testid={`service-${service.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${service.bgColor} flex items-center justify-center`}>
                        <Icon className={`h-5 w-5 ${service.color}`} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{service.name}</CardTitle>
                        <p className="text-xs text-muted-foreground">{service.provider}</p>
                      </div>
                    </div>
                    <Badge 
                      className={service.pricingType === 'free' 
                        ? 'bg-green-500/10 text-green-500 border-green-500/20' 
                        : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                      }
                    >
                      {service.pricing}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">{service.description}</p>
                  
                  {/* Search ID */}
                  <div className="p-3 rounded-lg bg-muted/30 border border-border">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">Model / Search ID:</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2"
                        onClick={() => {
                          navigator.clipboard.writeText(service.searchId);
                          toast.success('تم نسخ المعرف');
                        }}
                      >
                        نسخ
                      </Button>
                    </div>
                    <code className="text-sm font-mono text-primary">{service.searchId}</code>
                  </div>
                  
                  {/* Pricing */}
                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span>{service.priceDetails}</span>
                  </div>
                  
                  {/* API Key Type */}
                  <div className="flex items-center gap-2 text-sm">
                    <Key className="h-4 w-4 text-muted-foreground" />
                    <span>{service.apiKeyType}</span>
                  </div>
                  
                  {/* Features */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">الميزات:</p>
                    <ul className="text-xs space-y-1">
                      {service.features.slice(0, service.id === 'ideogram-ai' ? 7 : 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Status & Docs */}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="text-xs text-green-500">نشط</span>
                      {service.isConfigured && (
                        <Badge className="bg-green-500/10 text-green-500 text-xs">مُفعّل</Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {service.studioUrl && (
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={() => window.location.href = service.studioUrl}
                          className="bg-pink-500 hover:bg-pink-600"
                        >
                          <Sparkles className="h-3 w-3 mr-1" />
                          استوديو
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => window.open(service.docsUrl, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        الوثائق
                      </Button>
                    </div>
                  </div>
                  
                  {/* Config Note */}
                  {service.configNote && (
                    <div className="text-xs text-green-600 bg-green-50 p-2 rounded flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      {service.configNote}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Payment Section */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            بوابات الدفع
          </CardTitle>
          <CardDescription>Payment Gateways - لشحن الرصيد وتجديد الاشتراك</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {paymentOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <div 
                key={index}
                className={`p-4 rounded-lg border ${option.recommended ? 'border-primary/50 bg-primary/5' : 'border-border bg-muted/30'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg ${option.recommended ? 'bg-primary/20' : 'bg-muted'} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`h-5 w-5 ${option.recommended ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{option.name}</h4>
                        {option.recommended && (
                          <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">
                            موصى به
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                      
                      {/* Credentials Info */}
                      <div className="mt-2 p-2 rounded bg-background/50 border border-border">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Key className="h-3 w-3" />
                          <span>{option.credentials}</span>
                        </p>
                        {option.note && (
                          <p className="text-xs text-primary mt-1">
                            📍 {option.note}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button 
                      variant={option.recommended ? 'default' : 'outline'}
                      onClick={() => window.open(option.url, '_blank')}
                      data-testid={`payment-${index}`}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      الدفع
                    </Button>
                    {option.signupUrl && (
                      <Button 
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(option.signupUrl, '_blank')}
                        className="text-xs"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        إنشاء حساب
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Info Box */}
          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-500 mb-1">ملاحظة مهمة</p>
                <p className="text-muted-foreground">
                  نوصي باستخدام <strong>Emergent Universal Key</strong> لأنه يوفر مفتاح واحد لجميع خدمات AI 
                  (Claude, OpenAI, Gemini) مع إدارة مركزية للفواتير والاستخدام.
                </p>
                <p className="text-muted-foreground mt-2">
                  للشحن: اذهب إلى Profile → Universal Key → Add Balance
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Technical Info */}
      <Card className="bg-muted/30 border-border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-5 w-5" />
            معلومات تقنية
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Environment Variables:</h4>
              <code className="block p-2 rounded bg-background border border-border text-xs font-mono">
                EMERGENT_LLM_KEY=sk-emergent-xxxxx
              </code>
            </div>
            <div>
              <h4 className="font-medium mb-2">API Endpoints:</h4>
              <ul className="space-y-1 text-xs font-mono">
                <li>POST /api/chat - المحادثة مع Nicole</li>
                <li>POST /api/images/generate - توليد الصور</li>
                <li>GET /api/search/suppliers - البحث عن موردين</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
